/**
 * The run list a partner is handed: each day's routes, and each route's stops in the order it
 * runs them, with where and when.
 *
 * Pure on purpose, like the haulage plan beside it (haulagePlanExport.ts), and for the same
 * reason: this leaves the building. What goes on the page is decided here as plain strings, where
 * a test can hold it still, and runListPdf.ts only draws what it is given.
 *
 * It carries what a partner's own login is sent (supabase/functions/collections/partner.ts) and
 * no more: no cost, revenue, load factor or notes. Volume is on it for every login, in parcels
 * and in UK pallets, because the day's stops carry both.
 */
import { format, startOfDay } from "date-fns"

import { formatDayLong, formatDayMonthYear, formatDayShortYear } from "@/lib/dateFormats"
import { formatRouteTime, formatTimeWindow } from "@/lib/haulagePlanExport"
import { type RouteLoadFactorParams, stopUkPallets, toCapacityStop } from "@/lib/loadFactor"
import { instanceStopFor } from "@/lib/planningRoutes"
import {
  calculateDurationMinutes,
  findVehicleType,
  resolveRouteKind,
} from "@/lib/routeCostCalculation"
import { formatDuration } from "@/lib/routeDelayDisplay"
import { getRouteKindBadge } from "@/lib/routeKindDisplay"
import { getSlotTypeLabel } from "@/lib/slotTypeDisplay"
import { getPlannedRouteStatusDisplay } from "@/lib/statusDisplay"
import type { PlannedRoute, PlannedStop, RouteInstance, VehicleType } from "@/types/collections"

/** The stop columns every run list has, in the order a row's cells are written. */
export const RUN_LIST_STOP_HEADERS = [
  "#",
  "Arrive",
  "Depart",
  "Stop",
  "Site",
  "Address",
  "Postcode",
]

/** The volume columns, which follow the rest where the caller can supply them. */
export const RUN_LIST_UNITS_HEADER = "Units"
export const RUN_LIST_UK_PALLETS_HEADER = "UK pallets"

/** One route on one day. */
export interface RunListRoute {
  code: string
  /** "Mon 12 Oct 2026", on every route so a sheet still says its day once it is pulled out. */
  day: string
  /** Start to end, with the day marked where the route finishes after the one it starts on. */
  window: string
  /** Set apart from the code because they change how the sheet should be read: Draft, Stand-by. */
  tags: string[]
  /** Who runs it and with what, as labelled facts. Only the ones the route has. */
  details: { label: string; value: string }[]
  /** A row per stop, cells in the list's `stopHeaders` order. */
  stops: string[][]
  /**
   * The route's volume, one figure under each volume column. Null where the list has no volume
   * columns or the route has nothing forecast, so a sheet never closes on a row of blanks.
   */
  totals: string[] | null
}

export interface RunListDay {
  /** "Monday 12 October 2026" */
  heading: string
  routes: RunListRoute[]
}

export interface RunList {
  /** The day, or the first and last day, the list actually holds. */
  period: string
  /** The one operator every route shares, or null where the list mixes them. */
  operator: string | null
  routeCount: number
  /** When it was drawn up, "12 Oct 2026 14:30": a plan moves, so a copy has to say how old it is. */
  generated: string
  /** The stop columns this list has: the standing ones, then whichever volume columns apply. */
  stopHeaders: string[]
  days: RunListDay[]
}

/** The location fields worth preferring over the copies denormalised onto a stop. */
type SiteLike = {
  site_name?: string | null
  address?: string | null
  postcode?: string | null
}

export interface RunListContext {
  getSite?: (locationId: string) => SiteLike | undefined
  /** Names a vehicle type as the app does; without it the route's own reference is printed. */
  vehicleTypes?: VehicleType[]
  /** The moment the list is drawn up. Passed in so a test can fix it. */
  now?: Date
  /** A stop's forecast volume in parcels. Without it the list has no Units column. */
  stopUnits?: (route: PlannedRoute, stop: PlannedStop) => number
  /**
   * The same stop in UK pallet equivalents. Left out by a caller that cannot work it out, and
   * the column goes with it rather than standing empty.
   */
  stopUkPallets?: (route: PlannedRoute, stop: PlannedStop) => number
}

/**
 * The two volume columns, read off the day's own record of each stop.
 *
 * That record is what every login is sent, a partner's included, so the figures do not depend on
 * the site list or on who is asking: the parcels forecast for the stop, and the loading units
 * the forecast sync worked those out to, converted to UK pallets as the timeline and monitoring
 * convert them. `config` carries the conversion ratios; a login that is not sent it gets the
 * standard ones.
 *
 * A stop on a day with no instance yet has neither figure.
 */
export function runListVolume(
  instanceOf: (route: PlannedRoute) => RouteInstance | undefined,
  load: Pick<RouteLoadFactorParams, "locations" | "config"> = {},
): Required<Pick<RunListContext, "stopUnits" | "stopUkPallets">> {
  return {
    stopUnits: (route, stop) => instanceStopFor(instanceOf(route), stop)?.volume_forecast ?? 0,
    stopUkPallets: (route, stop) => {
      const dayStop = instanceStopFor(instanceOf(route), stop)
      return dayStop ? stopUkPallets(toCapacityStop(dayStop), load) : 0
    },
  }
}

/** Parcels as a whole number with its thousands marked; blank for none, which is not a forecast. */
const formatUnits = (units: number): string =>
  units > 0 ? Math.round(units).toLocaleString("en-GB") : ""

/** UK pallets to one decimal place, which is as fine as a ratio of 3.43 to the bag deserves. */
const formatUkPallets = (pallets: number): string => (pallets > 0 ? pallets.toFixed(1) : "")

/**
 * Whether a route belongs on a run list at all.
 *
 * A cancelled route is not a run. The list on screen keeps it, because a planner needs to see
 * what was taken out; a partner handed it as a sheet would send a driver.
 */
export function isRunListRoute(route: PlannedRoute): boolean {
  return route.status !== "cancelled" && route.routeInstanceStatus !== "cancelled"
}

function buildRoute(route: PlannedRoute, context: RunListContext): RunListRoute {
  const stops = [...route.stops].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))

  const vehicleType = findVehicleType(context.vehicleTypes, route.vehicleType)
  const vehicle = [
    vehicleType?.display_name || vehicleType?.name || route.vehicleType,
    route.vehicleReg,
  ].filter(Boolean)

  const duration = calculateDurationMinutes(route.plannedStartTime, route.plannedEndTime)

  const units = stops.map((stop) => context.stopUnits?.(route, stop) ?? 0)
  const ukPallets = stops.map((stop) => context.stopUkPallets?.(route, stop) ?? 0)
  const sum = (values: number[]) => values.reduce((total, value) => total + value, 0)
  const totals = [
    ...(context.stopUnits ? [formatUnits(sum(units))] : []),
    ...(context.stopUkPallets ? [formatUkPallets(sum(ukPallets))] : []),
  ]

  const details = [
    { label: "Operator", value: route.operator ?? "" },
    { label: "Depot", value: route.depot ?? "" },
    { label: "Vehicle", value: vehicle.join(", ") },
    { label: "Crew", value: (route.crewSize ?? 1) > 1 ? `${route.crewSize} drivers` : "" },
    { label: "Driver", value: route.driverName ?? "" },
    { label: "Duration", value: duration ? formatDuration(Math.round(duration)) : "" },
  ].filter((detail) => detail.value)

  const kind = getRouteKindBadge(resolveRouteKind(route.route_kind))
  const tags = [
    // Live is the ordinary case and says nothing; a draft is a plan that may still move.
    route.status === "draft" ? getPlannedRouteStatusDisplay(route.status).label : null,
    kind,
  ].filter((tag): tag is string => !!tag)

  return {
    code: route.code,
    day: formatDayShortYear(route.date),
    window: formatTimeWindow(route.plannedStartTime, route.plannedEndTime, route.date),
    tags,
    details,
    stops: stops.map((stop, index) => {
      const site = context.getSite?.(stop.locationId)
      return [
        (index + 1).toString(),
        formatRouteTime(stop.plannedArrival, route.date),
        formatRouteTime(stop.plannedDeparture, route.date),
        getSlotTypeLabel(stop.slotType),
        (site?.site_name || stop.name || "").trim() || "Unknown site",
        (site?.address || stop.address || "").trim(),
        (site?.postcode || stop.postcode || "").trim(),
        ...(context.stopUnits ? [formatUnits(units[index])] : []),
        ...(context.stopUkPallets ? [formatUkPallets(ukPallets[index])] : []),
      ]
    }),
    totals: totals.some(Boolean) ? totals : null,
  }
}

/**
 * The whole list: days in date order, and within a day the routes in the order they are given.
 *
 * The caller's order is kept so the document reads the way the screen it came from does, sort
 * and all. Cancelled routes are dropped (isRunListRoute).
 */
export function buildRunList(routes: PlannedRoute[], context: RunListContext = {}): RunList {
  const running = routes.filter(isRunListRoute)

  const byDay = new Map<number, PlannedRoute[]>()
  for (const route of running) {
    const day = startOfDay(route.date).getTime()
    byDay.set(day, [...(byDay.get(day) ?? []), route])
  }
  const dayKeys = [...byDay.keys()].sort((a, b) => a - b)

  const operators = new Set(running.map((route) => route.operator ?? ""))

  const now = context.now ?? new Date()
  const first = dayKeys.length > 0 ? new Date(dayKeys[0]) : null
  const last = dayKeys.length > 0 ? new Date(dayKeys[dayKeys.length - 1]) : null

  return {
    period:
      first && last
        ? first.getTime() === last.getTime()
          ? formatDayShortYear(first)
          : `${formatDayShortYear(first)} to ${formatDayShortYear(last)}`
        : "",
    operator: operators.size === 1 ? [...operators][0] || null : null,
    routeCount: running.length,
    generated: `${formatDayMonthYear(now)} ${format(now, "HH:mm")}`,
    stopHeaders: [
      ...RUN_LIST_STOP_HEADERS,
      ...(context.stopUnits ? [RUN_LIST_UNITS_HEADER] : []),
      ...(context.stopUkPallets ? [RUN_LIST_UK_PALLETS_HEADER] : []),
    ],
    days: dayKeys.map((day) => ({
      heading: formatDayLong(new Date(day)),
      routes: (byDay.get(day) ?? []).map((route) => buildRoute(route, context)),
    })),
  }
}

/** The file a run list is saved as: the days it holds, and who it is for where that is one name. */
export function runListFilename(runList: RunList, rangeSlug: string): string {
  const operator = (runList.operator ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
  return ["run_list", operator, rangeSlug, `${runList.routeCount}_routes`]
    .filter(Boolean)
    .join("_")
    .concat(".pdf")
}
